// The child-detail panel (tabs, one AG Grid per child relationship, many-to-many link/unlink) for
// the Generic CRUD engine. Extracted out of generic_crud.tsx so the exact same component renders
// both docked (below the master grid, generic_crud.tsx) and undocked (in its own popped-out
// browser window, generic_crud_detail_window.tsx) - REQ-DU-013/014: same component in both modes,
// no forked duplicate implementation. See CRUD_engine_test_plan.md "Phase 5 - Undock & Sync".
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import type { CellValueChangedEvent, ColDef } from "ag-grid-community";
import type { GetContextMenuItemsParams } from "ag-grid-enterprise";
import { Box, Button, HStack, IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Tab, TabList, TabPanel, TabPanels, Tabs, Text, VStack } from "@chakra-ui/react";
import { FaLink, FaLinkSlash, FaPlus, FaTrash } from "react-icons/fa6";
import { useApi } from "../../hooks/useApi";
import { NotificationContext } from "../NotificationPane";
import { EDB_CUSTOM_withHeaderTooltips } from "../../util/AgGridFunctions";
import type { ActionMeta, ChildMeta, EntityMeta } from "../../schemas/crudMeta";
import type { NotificationContextType } from "../../types";
import { type Row, buildColumnDefs, buildRowContextMenu, newTempId, buildSharedDefaultColDef } from "./shared";
import { useColumnDefaults } from "../../hooks/useColumnDefaults";

type ChildDetailPanelProps = {
  selectedEntity: string;
  selectedEntityMeta: EntityMeta;
  focusedRow: Row | null;
  activeChildTab: number;
  onActiveChildTabChange: (index: number) => void;
  onRunAction: (entityName: string, entityMeta: EntityMeta, action: ActionMeta, row: Row) => void;
  // Real metadata for whichever child entity is on the active tab, looked up by the parent from
  // its metaGraph (same source generic_crud.tsx always used - no separate fetch here).
  activeChildMeta: EntityMeta | null;
  // Supports the create-child custom action (CRA-01, dispatched by the parent since it also
  // changes which master row/tab is focused): a blank row pre-filled with just the FK, which this
  // panel appends to its own childRows once its next fetch for that tab/row resolves.
  pendingBlankChild?: Row | null;
  onPendingBlankChildConsumed?: () => void;
  // Bumped by the parent to force loadChildRows to re-run even when focusedRow/activeChildTab
  // are unchanged (e.g. a create-child action re-targets the tab/row that's already focused).
  reloadNonce?: number;
};

export default function ChildDetailPanel({
  selectedEntity,
  selectedEntityMeta,
  focusedRow,
  activeChildTab,
  onActiveChildTabChange,
  onRunAction,
  activeChildMeta,
  pendingBlankChild,
  onPendingBlankChildConsumed,
  reloadNonce,
}: ChildDetailPanelProps) {
  const { getGenericCrudChildRows, putGenericCrudRow, deleteGenericCrudRow, linkGenericCrudChild, unlinkGenericCrudChild, getGenericCrudRows } = useApi();
  const columnDefaults = useColumnDefaults();
  const { pushNotification } = useContext<NotificationContextType | null>(NotificationContext)!;

  const [childRows, setChildRows] = useState<Row[]>([]);
  const [childLoading, setChildLoading] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkCandidates, setLinkCandidates] = useState<Row[]>([]);
  const [linkSearch, setLinkSearch] = useState("");

  const activeChild: ChildMeta | undefined = selectedEntityMeta.children[activeChildTab];

  const loadChildRows = useCallback(async () => {
    if (!focusedRow || !activeChild || focusedRow.__tempId) {
      setChildRows([]);
      return;
    }
    const parentId = focusedRow[selectedEntityMeta.pk];
    if (parentId == null) return;
    try {
      setChildLoading(true);
      const data = await getGenericCrudChildRows(selectedEntity, Number(parentId), activeChild.childEntity);
      const pending = pendingBlankChild;
      onPendingBlankChildConsumed?.();
      setChildRows(pending ? [...data.rows, pending] : data.rows);
    } catch (e: any) {
      pushNotification(e?.body?.error || e?.message || "Failed to load child rows.", "error");
    } finally {
      setChildLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity, focusedRow, activeChild, selectedEntityMeta, getGenericCrudChildRows, pushNotification]);

  useEffect(() => {
    loadChildRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadChildRows, reloadNonce]);

  const handleAddChild = useCallback(() => {
    if (!focusedRow || !activeChild || activeChild.isManyToMany) return;
    const parentId = Number(focusedRow[selectedEntityMeta.pk]);
    setChildRows((prev) => [...prev, { __tempId: newTempId(), [activeChild.fkColumn!]: parentId }]);
  }, [focusedRow, activeChild, selectedEntityMeta]);

  const handleChildCellValueChanged = useCallback(
    async (event: CellValueChangedEvent<Row>) => {
      if (!activeChild || !activeChildMeta || !event.data) return;
      const row = event.data;
      try {
        const result = await putGenericCrudRow(activeChild.childEntity, row);
        setChildRows((prev) =>
          prev.map((r) =>
            r === row || (row.__tempId && r.__tempId === row.__tempId)
              ? { ...r, [activeChildMeta.pk]: result.sk, row_version: result.row_version, __tempId: undefined }
              : r,
          ),
        );
      } catch (e: any) {
        pushNotification(e?.body?.error || e?.message || "Failed to save child row.", "error");
        await loadChildRows();
      }
    },
    [activeChild, activeChildMeta, putGenericCrudRow, loadChildRows, pushNotification],
  );

  const handleDeleteChild = useCallback(
    async (row: Row) => {
      if (!activeChild || !activeChildMeta) return;
      if (row.__tempId) {
        setChildRows((prev) => prev.filter((r) => r.__tempId !== row.__tempId));
        return;
      }
      const id = Number(row[activeChildMeta.pk]);
      if (!window.confirm(`Delete this ${activeChildMeta.labelSingular.toLowerCase()}?`)) return;
      try {
        await deleteGenericCrudRow(activeChild.childEntity, id);
        await loadChildRows();
      } catch (e: any) {
        pushNotification(e?.body?.error || e?.message || "Failed to delete row.", "error");
      }
    },
    [activeChild, activeChildMeta, deleteGenericCrudRow, loadChildRows, pushNotification],
  );

  const handleOpenLinkModal = useCallback(async () => {
    if (!activeChild) return;
    setLinkModalOpen(true);
    setLinkSearch("");
    try {
      const data = await getGenericCrudRows(activeChild.childEntity, { limit: 500 });
      setLinkCandidates(data.rows);
    } catch (e: any) {
      pushNotification(e?.body?.error || e?.message || "Failed to load candidates.", "error");
    }
  }, [activeChild, getGenericCrudRows, pushNotification]);

  const handleLinkCandidate = useCallback(
    async (candidate: Row) => {
      if (!focusedRow || !activeChild || !activeChildMeta) return;
      const parentId = Number(focusedRow[selectedEntityMeta.pk]);
      const childId = Number(candidate[activeChildMeta.pk]);
      try {
        await linkGenericCrudChild(selectedEntity, parentId, activeChild.childEntity, childId);
        pushNotification("Linked.", "success");
        setLinkModalOpen(false);
        await loadChildRows();
      } catch (e: any) {
        pushNotification(e?.body?.error || e?.message || "Failed to link.", "error");
      }
    },
    [selectedEntity, focusedRow, activeChild, activeChildMeta, selectedEntityMeta, linkGenericCrudChild, loadChildRows, pushNotification],
  );

  const handleUnlink = useCallback(
    async (row: Row) => {
      if (!focusedRow || !activeChild || !activeChildMeta) return;
      const parentId = Number(focusedRow[selectedEntityMeta.pk]);
      const childId = Number(row[activeChildMeta.pk]);
      try {
        await unlinkGenericCrudChild(selectedEntity, parentId, activeChild.childEntity, childId);
        pushNotification("Unlinked.", "success");
        await loadChildRows();
      } catch (e: any) {
        pushNotification(e?.body?.error || e?.message || "Failed to unlink.", "error");
      }
    },
    [selectedEntity, focusedRow, activeChild, activeChildMeta, selectedEntityMeta, unlinkGenericCrudChild, loadChildRows, pushNotification],
  );

  const defaultColDef = useMemo(() => buildSharedDefaultColDef(columnDefaults), [columnDefaults]);

  const childColumnDefs = useMemo<ColDef<Row>[]>(() => {
    if (!activeChildMeta || !activeChild) return [];
    const dataColumns = EDB_CUSTOM_withHeaderTooltips(buildColumnDefs(activeChildMeta, !activeChild.isManyToMany, columnDefaults));
    const actionsColumn: ColDef<Row> = {
      headerName: "",
      colId: "actions",
      pinned: "left",
      editable: false,
      filter: false,
      sortable: false,
      resizable: false,
      suppressHeaderMenuButton: true,
      width: 56,
      cellRenderer: (p: any) =>
        p.data ? (
          <IconButton
            aria-label={activeChild.isManyToMany ? "Unlink" : "Delete row"}
            icon={activeChild.isManyToMany ? <FaLinkSlash /> : <FaTrash />}
            size="xs"
            variant="ghost"
            colorScheme={activeChild.isManyToMany ? "gray" : "red"}
            onClick={() => (activeChild.isManyToMany ? handleUnlink(p.data) : handleDeleteChild(p.data))}
          />
        ) : null,
    };
    return [actionsColumn, ...dataColumns];
  }, [activeChildMeta, activeChild, handleUnlink, handleDeleteChild, columnDefaults]);

  const childContextMenuItems = useCallback(
    (p: GetContextMenuItemsParams<Row>) =>
      activeChild && activeChildMeta
        ? buildRowContextMenu(
            activeChild.childEntity,
            activeChildMeta,
            activeChild.isManyToMany ? "Unlink" : `Delete ${activeChildMeta.labelSingular.toLowerCase()}`,
            activeChild.isManyToMany ? handleUnlink : handleDeleteChild,
            onRunAction,
          )(p)
        : (p.defaultItems ?? []),
    [activeChild, activeChildMeta, handleUnlink, handleDeleteChild, onRunAction],
  );

  const filteredCandidates = linkCandidates.filter((c) => JSON.stringify(c).toLowerCase().includes(linkSearch.toLowerCase()));

  if (selectedEntityMeta.children.length === 0) return null;

  return (
    <>
      {/* GRA-14: unselected state, not a confusingly empty panel. */}
      {!focusedRow && <Text color="gray.500">Select a {selectedEntityMeta.labelSingular.toLowerCase()} to see its details.</Text>}
      {focusedRow && (
        <Tabs index={activeChildTab} onChange={onActiveChildTabChange} size="sm">
          <TabList>
            {selectedEntityMeta.children.map((c) => (
              <Tab key={c.childEntity}>{c.tabLabel}</Tab>
            ))}
          </TabList>
          <TabPanels>
            {selectedEntityMeta.children.map((c) => (
              <TabPanel key={c.childEntity} px={0}>
                <HStack justifyContent="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.600">
                    {c.tabLabel} for {String(focusedRow[selectedEntityMeta.displayField])}
                  </Text>
                  {c.isManyToMany ? (
                    <Button size="xs" leftIcon={<FaLink />} onClick={handleOpenLinkModal}>
                      Link existing…
                    </Button>
                  ) : (
                    <Button size="xs" leftIcon={<FaPlus />} onClick={handleAddChild}>
                      Add {activeChildMeta?.labelSingular ?? "row"}
                    </Button>
                  )}
                </HStack>
                {/* GRA-15: empty state still offers the add affordance above. */}
                {!childLoading && childRows.length === 0 && (
                  <Text color="gray.500" fontSize="sm">
                    No {c.tabLabel.toLowerCase()} yet — {c.isManyToMany ? "link one above" : "add one above"}.
                  </Text>
                )}
                {childRows.length > 0 && activeChildMeta && (
                  <Box className="ag-theme-quartz" w="100%">
                    <AgGridReact<Row>
                      domLayout="autoHeight"
                      rowData={childRows}
                      columnDefs={childColumnDefs}
                      defaultColDef={defaultColDef}
                      tooltipShowDelay={200}
                      getRowId={(params) => params.data.__tempId ?? String(params.data[activeChildMeta.pk])}
                      onCellValueChanged={handleChildCellValueChanged}
                      getContextMenuItems={childContextMenuItems}
                      animateRows
                    />
                  </Box>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}

      <Modal isOpen={linkModalOpen} onClose={() => setLinkModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Link existing {activeChildMeta?.labelSingular.toLowerCase()}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <Input placeholder="Search…" value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} mb={3} />
            <VStack align="stretch" spacing={1} maxH="300px" overflow="auto">
              {activeChildMeta &&
                filteredCandidates.map((c) => (
                  <Button key={String(c[activeChildMeta.pk])} justifyContent="flex-start" variant="ghost" size="sm" onClick={() => handleLinkCandidate(c)}>
                    {String(c[activeChildMeta.displayField])}
                  </Button>
                ))}
              {filteredCandidates.length === 0 && (
                <Text color="gray.500" fontSize="sm">
                  No matches.
                </Text>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
